import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';

const logger = Logger.getLogger('serverApiService');
let SHARED_AUTH_KEY_ACROSS_REQUESTS;

function assertResponseHasNoErrors(response) {
    if (!response.error || !response.error.codeKey) {
        return;
    }

    const error = new Error(response.error.codeKey);

    error.code = response.error.codeKey;
    error.responseBody = response;

    throw error;
}

function getHeaders(authToken) {
    const headers = {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
    };

    if (authToken) {
        headers['X-TOKEN'] = authToken;
    }

    return headers;
}

// `withAuth: false` is for routes wapi serves anonymously (the carriers list,
// wanderu/wapi#544). wapi verifies any token it is sent even on an open route, so
// sending the shared one anyway is the only way such a request can 401. With no
// token there is nothing to refresh, so a 401 is not retried either.
async function get(url, init = {}, opts: { withAuth?: boolean } = {}) {
    const { withAuth = true } = opts;
    const token = withAuth
        ? await getAuthenticationToken()
        : '';
    try {
        const response = await fetch(url, {
            method: 'get',
            ...init,
            headers: {
                ...getHeaders(token),
                ...init.headers,
            },
        });

        if (withAuth && isUnauthorized(response)) {
            clearToken();
            return get(url, init, opts);
        }

        // Anonymous path: no token to refresh, so fail on the status before
        // touching the body. An edge error page is not JSON, and a JSON body with
        // no codeKey would otherwise pass as a success.
        if (!withAuth && (isUnauthorized(response) || isForbidden(response))) {
            assertNotRejected(response);
        }

        const json = await response.json();

        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        // decorate the error to get more exact reasons on the fail
        logger.warn('server get error', ex, {
            init,
            // Ideally we would log the response headers, but we need to know if the request
            // actually has completed in a way we can get that information.
            resHeaders: ex?.response?.headers,
            status: (ex as { status?: number })?.status,
            tokenPresent: token.length > 0,
            url,
            withAuth,
        });
        throw ex;
    }
}

// `del` and `post` take no `withAuth`: the only anonymous POST is auth.json,
// which the client sends itself. Add the option here if a server-side caller
// ever needs an anonymous write.

async function del(url, init = {}) {
    const token = await getAuthenticationToken();
    try {
        const response = await fetch(url, {
            method: 'delete',
            ...init,
            headers: {
                ...getHeaders(token),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            clearToken();
            return del(url, init);
        }

        const json = await response.json();
        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('server delete error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            tokenPresent: token.length > 0,
            url,
        });
        throw ex;
    }
}

async function post(url, init = {}) {
    const token = await getAuthenticationToken();
    try {
        const response = await fetch(url, {
            method: 'post',
            ...init,
            body: typeof init.body === 'string'
                ? init.body
                : JSON.stringify(init.body),
            headers: {
                ...getHeaders(token),
                ...init.headers,
            },
        });

        if (isUnauthorized(response)) {
            clearToken();
            return post(url, init);
        }

        const json = await response.json();
        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('server post error', ex, {
            init,
            resHeaders: ex?.response?.headers,
            tokenPresent: token.length > 0,
            url,
        });
        throw ex;
    }
}

async function postAnonymous(url, init = {}) {
    try {
        const response = await fetch(url, {
            method: 'post',
            ...init,
            body: typeof init.body === 'string'
                ? init.body
                : JSON.stringify(init.body),
            headers: {
                ...getHeaders(),
                ...init.headers,
            },
        });

        const json = await response.json();
        assertResponseHasNoErrors(json);

        return json;
    } catch (ex) {
        logger.warn('post anonymous', ex, {
            init,
            resHeaders: ex?.response?.headers,
            url,
        });
        throw ex;
    }
}

async function getAuthenticationToken() {
    if (!SHARED_AUTH_KEY_ACROSS_REQUESTS) {
        SHARED_AUTH_KEY_ACROSS_REQUESTS = await requestAnonymousAuthToken();
    }

    return SHARED_AUTH_KEY_ACROSS_REQUESTS;
}

async function requestAnonymousAuthToken() {
    try {
        const response = await postAnonymous(buildWapiUrl('auth.json'), {
            body: {
                credentials: {
                    ...appSettings.config.wapi.credentials,
                    type: 'user',
                    username: 'anonymous',
                },
            },
        });

        return response.result.token;
    } catch (ex) {
        logger.error('request anonymous auth token', ex, {
            resHeaders: ex?.response?.headers,
        });
        throw ex;
    }
}

function clearToken() {
    SHARED_AUTH_KEY_ACROSS_REQUESTS = undefined;
}

function isUnauthorized(response = {}) {
    return response.status === 401;
}

function isForbidden(response: { status?: number } = {}) {
    return response.status === 403;
}

// For responses whose body may not be JSON (a Cloudflare or ALB error page):
// throws an error that names the HTTP status, so the log line reads as an auth
// failure rather than "Unexpected token <".
function assertNotRejected(response: { status?: number }): never {
    const error: Error & { code?: string, status?: number } = new Error(
        `wapi rejected the request with HTTP ${response.status}`,
    );

    error.code = `HTTP_${response.status}`;
    error.status = response.status;

    throw error;
}

function buildWapiUrl(path) { // would have put this in wapiservice, but can't have circular references
    return appSettings.config.wapi.urlRoot + path;
}

// No federated re-auth on the server: there is no provider SDK and no
// localStorage mirror. Accept and ignore the registration so a shared
// composition root can register the seam without an SSR-specific guard.
// Parity stub: the server target holds no per-request client state to seed.
function initService() {
    return undefined;
}

function onReauthenticate() {
    return undefined;
}

export default {
    delete: del,
    get,
    getAuthenticationToken,
    initService,
    onReauthenticate,
    post,
};
